import type { BOLHistoryItem } from '@/api/hooks/useOrderHistory'
import {
  formatTimestamp,
  getMappingDisplay,
} from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/OrderChangeSummary/utils'
import { ArrowRightOutlined, FileTextOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Table, type TableColumnsType, theme } from 'antd'
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

interface BOLCardProps {
  bol: BOLHistoryItem
}

export function BOLCard({ bol }: BOLCardProps) {
  const [expanded, setExpanded] = useState(false)
  const { token } = theme.useToken()
  const isError = bol.status === 'error'
  const isRejected = bol.status === 'rejected'
  const statusColor = isError ? token.colorError : token.colorWarning

  // BOL detail columns for expanded view
  const detailColumns: TableColumnsType<
    (typeof bol.origin_bol.details)[number]
  > = [
    {
      title: 'Product',
      dataIndex: 'product',
      render: (p) => getMappingDisplay(p) ?? '—',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      render: (s) => getMappingDisplay(s) ?? '—',
    },
    {
      title: 'Net Vol',
      dataIndex: 'net_volume',
      render: (v: number) => (v ? `${v.toLocaleString()} gal` : '—'),
    },
    {
      title: 'Gross Vol',
      dataIndex: 'gross_volume',
      render: (v: number) => (v ? `${v.toLocaleString()} gal` : '—'),
    },
    {
      title: 'Price Type',
      dataIndex: 'price_type',
      render: (v) => v ?? '—',
    },
  ]

  return (
    <Horizontal gap={12} style={{ position: 'relative' }}>
      {/* Timeline dot */}
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor:
            isError || isRejected ? statusColor : token.colorTextQuaternary,
          flexShrink: 0,
          marginTop: 18,
          zIndex: 1,
        }}
      />

      <Card
        size='small'
        style={{
          flex: 1,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          borderLeft:
            isError || isRejected ? `3px solid ${statusColor}` : undefined,
          backgroundColor:
            isError || isRejected
              ? `color-mix(in srgb, ${statusColor} 5%, transparent)`
              : undefined,
        }}
        styles={{ body: { padding: 12 } }}
        onClick={() => setExpanded(!expanded)}
      >
        <Vertical gap={8}>
          {/* Row 1: BOL + BOL Number + Error Tag + Timestamp */}
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={8} alignItems='center'>
              <FileTextOutlined
                style={{ color: token.colorPrimary, display: 'flex' }}
              />
              <Texto weight='600'>BOL {bol.bol_number}</Texto>
            </Horizontal>
            <Horizontal gap={8} alignItems='center'>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                {formatTimestamp(bol.created_on)}
              </Texto>
              {expanded ? (
                <ChevronUp size={16} color={token.colorTextTertiary} />
              ) : (
                <ChevronDown size={16} color={token.colorTextTertiary} />
              )}
            </Horizontal>
          </Horizontal>

          {/* Row 2: Direction */}
          <Horizontal gap={6} alignItems='center'>
            <Texto weight='600' style={{ fontSize: 13 }}>
              {bol.origin_tenant_name}
            </Texto>
            <ArrowRightOutlined
              style={{ fontSize: 10, color: token.colorTextTertiary }}
            />
            <Texto weight='600' style={{ fontSize: 13 }}>
              {bol.destination_tenant_name ?? 'N/A'}
            </Texto>
          </Horizontal>

          {/* Row 3: Summary - Terminal, Date, Volume + Error indicator */}
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={12} alignItems='center'>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                Terminal: {bol.terminal_name ?? 'Unknown'}
              </Texto>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                Date: {new Date(bol.bol_date).toLocaleDateString()}
              </Texto>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                {bol.total_volume.toLocaleString()} gal ({bol.detail_count}{' '}
                items)
              </Texto>
            </Horizontal>
            {(isError || isRejected) && (
              <Horizontal gap={4} alignItems='center'>
                <AlertCircle size={12} color={statusColor} />
                <Texto style={{ fontSize: 12, color: statusColor }}>
                  {isRejected ? 'Rejected' : 'Error'}
                </Texto>
              </Horizontal>
            )}
          </Horizontal>

          {/* Expanded: Detail Table */}
          {expanded && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                marginTop: 12,
                paddingTop: 12,
                cursor: 'default',
              }}
            >
              <Table
                dataSource={bol.origin_bol.details}
                columns={detailColumns}
                size='small'
                pagination={false}
                rowKey={(_, idx) => String(idx)}
              />
            </div>
          )}
        </Vertical>
      </Card>
    </Horizontal>
  )
}
